/**
 * Encoding and compression utilities
 */
import pako from 'pako';

/**
 * Decompresses chunks of gzip compressed data
 */
export const decompressChunks = (compressedChunks: Uint8Array[]): string => {
  let decompressedString = '';

  compressedChunks.forEach((compressedChunk) => {
    // Decompress the chunk using gzip
    const decompressedChunk = pako.ungzip(compressedChunk, { to: 'string' });

    // Concatenate the decompressed chunk to the result string
    decompressedString += decompressedChunk;
  });

  return decompressedString;
};

/**
 * Compresses a string into chunks using gzip
 */
export const compressStringInChunks = (
  inputString: string,
  chunkSize: number = 1024 * 1024
): Uint8Array[] | string => {
  try {
    let offset = 0;
    const compressedChunks: Uint8Array[] = [];

    // Convert the input string to a Uint8Array
    const inputBytes = new TextEncoder().encode(inputString);

    while (offset < inputBytes.length) {
      // Get a chunk of data
      const chunk = inputBytes.slice(offset, offset + chunkSize);

      // Compress the chunk using gzip
      const compressedChunk = pako.gzip(chunk);

      // Add the compressed chunk to the array
      compressedChunks.push(compressedChunk);

      // Move to the next chunk
      offset += chunkSize;
    }

    return compressedChunks;
  } catch {
    return inputString;
  }
};

/**
 * Gets query text from compressed format
 */
export const getQueryText = (query: string): string => {
  try {
    const queryJson = JSON.parse(query);
    return decompressChunks(queryJson);
  } catch {
    return query;
  }
};

/**
 * Sets query text to compressed format
 */
export const setQueryText = (query: string): string => {
  try {
    return JSON.stringify(compressStringInChunks(query));
  } catch {
    return query;
  }
};

/**
 * Gets string size in kilobytes
 */
export function getStringSizeInKBytes(str: string): number {
  // Use the TextEncoder API to encode the string to UTF-8
  const encoder = new TextEncoder();
  const encodedString = encoder.encode(str);

  // Return the length of the encoded string (size in bytes)
  return encodedString.length / 1000;
}

/**
 * Checks if string size is under threshold
 */
export const checkThresholdSize = (str: string, threshold: number = 200): boolean => {
  try {
    return getStringSizeInKBytes(str) < threshold;
  } catch {
    return true;
  }
};

/**
 * Base36 encode a number
 */
export function base36encode(number: number): string {
  const alphabet = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  if (typeof number !== 'number' || !Number.isInteger(number)) {
    throw new TypeError('number must be an integer');
  }

  let base36 = '';
  let sign = '';

  if (number < 0) {
    sign = '-';
    number = -number;
  }

  if (number < alphabet.length) {
    return sign + (alphabet[number] ?? '');
  }

  while (number !== 0) {
    const i = number % alphabet.length;
    base36 = (alphabet[i] ?? '') + base36;
    number = Math.floor(number / alphabet.length);
  }

  return sign + base36;
}
